/**
 * Centralized database connection pool for Edge Runtime with Vercel Fluid Compute
 * Uses @neondatabase/serverless for PostgreSQL connections
 *
 * Vercel Fluid Compute allows global pool reuse across requests in the same instance.
 * The attachDatabasePool helper ensures idle connections are closed before suspension.
 */
import { neonConfig, Pool } from "@neondatabase/serverless";
import { attachDatabasePool } from "@vercel/functions";
import { env } from "@/env";
import { dbLogger } from "@/lib/logger";

const ciProxy = process.env.CI_NEON_WS_PROXY;
if (ciProxy && process.env.NODE_ENV !== "production") {
	if (!/^(?:127\.0\.0\.1|localhost):[1-9]\d{0,4}$/.test(ciProxy)) {
		throw new Error("CI_NEON_WS_PROXY must be a local host and port");
	}
	neonConfig.wsProxy = ciProxy;
	neonConfig.useSecureWebSocket = false;
	neonConfig.pipelineConnect = false;
}

// Global pool - Fluid Compute allows reuse across requests
// Shared by Better Auth, backup endpoints, and all database operations
const pool = new Pool({
	connectionString: env.POSTGRES_URL,
	max: 10, // Maximum pool size for serverless
	idleTimeoutMillis: 30_000, // Close idle connections after 30s (matches Better Auth requirements)
	connectionTimeoutMillis: 10_000,
});

// Attach pool to Fluid Compute lifecycle management
// This ensures idle connections are closed before instance suspension
attachDatabasePool(pool);

// Log pool errors
pool.on("error", (err: Error) => {
	dbLogger.error(
		{
			operation: "pool_error",
			error: err.message,
		},
		"Database pool error"
	);
});

/**
 * Get the database connection pool
 * Returns the global pool that Fluid Compute manages
 *
 * @returns {Pool} The PostgreSQL connection pool
 */
export function getPool(): Pool {
	return pool;
}

/**
 * Execute a database query with automatic connection management
 *
 * @param text SQL query text
 * @param params Query parameters
 * @returns Query result
 */
export async function query<T = unknown>(
	text: string,
	params?: unknown[]
): Promise<{ rows: T[]; rowCount: number | null }> {
	try {
		const result = await pool.query(text, params);
		return result;
	} catch (error) {
		dbLogger.error(
			{
				operation: "query_execution",
				query: text.slice(0, 100),
				error: error instanceof Error ? error.message : String(error),
			},
			"Database query failed"
		);
		throw error;
	}
}

/**
 * Note: No closePool() export
 *
 * With Vercel Fluid Compute, pool lifecycle is managed automatically via attachDatabasePool().
 * The pool persists across function invocations and Vercel handles cleanup on instance shutdown.
 *
 * Never call pool.end() - it would close the global shared pool and break all database operations.
 */
